import { useParams } from "../lib/router-context";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useWikipartyStore } from "../lib/games/wikiparty/store";
import { useGameSocket } from "../lib/useGameSocket";
import { Wikipage } from "../components/Wikipage";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";
import { ThemeToggle } from "../components/ThemeToggle";
import { TargetBadges } from "../components/TargetBadges";
import { Timer } from "../components/Timer";
import { ArticleCard, type ArticleCardProps } from "../components/ArticleCard";
import { ArticleActionButton } from "../components/ArticleActionButton";
import { PlayerList } from "../lib/games/wikiparty/components/PlayerList";
import { Timeline } from "../components/Timeline";
import { HomeLink } from "../components/HomeLink";
import { GeoBingoCard } from "../components/GeoBingoCard";
import { CopyLink } from "../components/CopyLink";
import { MobileNav } from "../components/navbar/NavbarMobile";
import { NavButtons } from "../components/navbar/NavbarButtons";
import { GameLoadingScreen } from "../components/GameLoadingScreen";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/Button";
import { SettingsToggle } from "../components/ui/SettingsToggle";
import { Select } from "../components/ui/Select";
import { SvgIcon } from "../components/ui/SvgIcon";
import gearIcon from "../assets/gear-icon.svg?raw";
import boltIcon from "../assets/bolt-icon.svg?raw";
import closeIcon from "../assets/close-icon.svg?raw";
import mapIcon from "../assets/map-icon.svg?raw";
import checkIcon from "../assets/check-icon.svg?raw";
import checkCircleIcon from "../assets/check-circle-icon.svg?raw";
import { SectionHeader } from "../components/SectionHeader";
import { GeometricGridBackground } from "../components/GeometricGridBackground";
import { Podium } from "../components/Podium";
import { ProfileAvatar } from "../components/ProfileAvatar";
import { ProviderBadge } from "../components/ProviderBadge";
import { DurationControls } from "../components/DurationControls";
import { HiddenSectionsControls } from "../components/HiddenSectionsControls";
import { TabButton, TabGroup } from "../components/ui/Tabs";
import { ArticleDialog } from "../components/ArticleDialog";
import { useCountryBlocker } from "../hooks/useCountryBlocker";
import { playFoundSound } from "../lib/sounds";
import { useTranslation } from "react-i18next";
import {
  useDraggable,
  useDroppable,
  DragDropProvider,
  DragOverlay,
} from "@dnd-kit/react";
import { trackEvent } from "../lib/analytics";
import { formatDurationCompact } from "../lib/utils/time";
import type { Phase } from "../lib/games/wikiparty/types";
import type { Article } from "../lib/games/wikidaily/types";
import {
  autocompleteWikipediaArticles,
  randomWikipediaArticles,
  searchWikipediaArticles,
} from "../lib/wikipediaApi";

const DraggableArticleCard = (
  props: ArticleCardProps & {
    dragId: string;
    isHost: boolean;
  },
) => {
  const { ref, isDragging } = useDraggable({
    id: props.dragId,
    disabled: !props.isHost,
    data: { article: props.article },
  });
  return (
    <ArticleCard
      {...props}
      ref={props.isHost ? ref : undefined}
      testId={`${props.dragId}-drag-card`}
      className={
        props.isHost
          ? `cursor-grab select-none active:cursor-grabbing ${isDragging ? "hidden" : ""}`
          : ""
      }
    />
  );
};

function DroppableSection({
  activeColorClassName,
  children,
  className,
  id,
}: {
  activeColorClassName: string;
  children: ReactNode;
  className?: string;
  id: string;
}) {
  const { ref, isDropTarget } = useDroppable({ id });

  return (
    <div
      ref={ref}
      data-testid={`${id}-article-drop-zone`}
      className={`${className ?? ""} rounded-xl ${
        isDropTarget
          ? `outline-2 outline-offset-4 outline-current ${activeColorClassName}`
          : ""
      }`}
    >
      {children}
    </div>
  );
}

export function WikiPartyPage() {
  const { lobbyId } = useParams() as { lobbyId: string };
  const isDark = useAppStore((state) => state.isDark);
  const currentPlayerId = useAppStore((state) => state.id);
  const restartGame = useAppStore((state) => state.restartGame);
  const kickPlayer = useAppStore((state) => state.kickPlayer);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const { send } = useGameSocket(lobbyId, useWikipartyStore);
  const { t } = useTranslation();

  const gameState = useWikipartyStore((state) => state.gameState);
  const lobbyState = useWikipartyStore((state) => state.lobbyState);
  const localState = useWikipartyStore((state) => state.localState);
  const updateSearch = useWikipartyStore((state) => state.update_search);
  const startGame = useWikipartyStore((state) => state.startGame);
  const endGame = useWikipartyStore((state) => state.endGame);
  const toggleAllowSearch = useWikipartyStore(
    (state) => state.toggleAllowSearch,
  );
  const setArticle = useWikipartyStore((state) => state.setArticle);
  const removeArticleFromSearch = useWikipartyStore(
    (state) => state.removeArticleFromSearch,
  );
  const removeArticle = useWikipartyStore((state) => state.removeArticle);
  const setTime = useWikipartyStore((state) => state.setTime);
  const [searchInput, setSearchInput] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState<Article[]>([]);
  const [activeAutocompleteIndex, setActiveAutocompleteIndex] = useState(-1);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [draggedArticle, setDraggedArticle] = useState<Article | null>(null);
  const [pendingArticleMove, setPendingArticleMove] = useState<{
    start: Article | null;
    targets: Article[];
  } | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autocompleteBlurTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const searchRequestRef = useRef(0);
  const suggestArticle = useWikipartyStore((state) => state.suggestArticle);
  const removeSuggestion = useWikipartyStore((state) => state.removeSuggestion);
  const toggleAllowCountries = useWikipartyStore(
    (state) => state.toggleAllowCountries,
  );
  const togglePlayUntilAllFinished = useWikipartyStore(
    (state) => state.togglePlayUntilAllFinished,
  );
  const setHiddenSections = useWikipartyStore(
    (state) => state.setHiddenSections,
  );
  const lang = useAppStore((state) => state.lang);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedArticle, setSelectedArticle] = useState<{
    title: string;
    description: string | null;
  } | null>(null);

  useEffect(
    () => () => {
      if (autocompleteBlurTimeoutRef.current)
        clearTimeout(autocompleteBlurTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!pendingArticleMove) return;

    if (
      pendingArticleMove.start?.id === (gameState?.start?.id ?? null) &&
      pendingArticleMove.targets.length === gameState?.target_articles.length &&
      pendingArticleMove.targets.every(
        (article, index) =>
          article.id === gameState?.target_articles[index]?.id,
      )
    ) {
      setPendingArticleMove(null);
      return;
    }

    const timeout = window.setTimeout(() => setPendingArticleMove(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [gameState?.start?.id, gameState?.target_articles, pendingArticleMove]);

  const navbarButtonsSettings = [
    NavButtons.players,
    NavButtons.wiki,
    NavButtons.search,
  ] as const;
  const [mobileTabSettings, setMobileTabSettings] = useState<
    (typeof navbarButtonsSettings)[number]
  >(NavButtons.wiki);

  const navbarButtonsIngame = [
    NavButtons.players,
    NavButtons.wiki,
    NavButtons.targets,
  ] as const;
  const [mobileTabIngame, setMobileTabIngame] = useState<
    (typeof navbarButtonsIngame)[number]
  >(NavButtons.wiki);

  const navbarButtonsEndscreen = [
    NavButtons.scoreboard,
    NavButtons.playerPaths,
    NavButtons.targetsFound,
  ] as const;
  const [mobileTabEndscreen, setMobileTabEndscreen] = useState<
    (typeof navbarButtonsEndscreen)[number]
  >(NavButtons.scoreboard);
  const [sortBy, setSortBy] = useState<"points" | "moves" | "time">("points");

  const tabsButtonsSearch = [
    NavButtons.search,
    NavButtons.suggestions,
  ] as const;
  const [tabSearchSuggestions, setTabSearchSuggestions] = useState<
    (typeof tabsButtonsSearch)[number]
  >(NavButtons.search);

  const allowCountries = gameState?.settings?.allow_countries;
  const { isLinkBlocked } = useCountryBlocker(allowCountries);
  const currentFoundCount =
    gameState?.players?.[currentPlayerId]?.found_targets?.length ?? 0;
  const prevFoundCount = useRef(currentFoundCount);
  const prevPhaseRef = useRef<Phase | null>(gameState?.phase ?? null);

  useEffect(() => {
    if (currentFoundCount > prevFoundCount.current) {
      playFoundSound();
    }
    prevFoundCount.current = currentFoundCount;
  }, [currentFoundCount]);

  //TODO: Phase logic  tracking could be handled in the backend but with umami this is an easy solution
  useEffect(() => {
    console.log(prevPhaseRef);
    if (prevPhaseRef.current === "Settings" && gameState?.phase === "Game") {
      trackEvent("game-start", {
        targetCount: gameState.target_articles.length.toString(),
        duration: gameState.game_duration.toString(),
        allowCountries: String(gameState.settings.allow_countries),
        allowSearch: String(gameState.settings.allow_search),
        hiddenSections: gameState.settings.hidden_sections.length.toString(),
        playUntilAllFinished: String(
          gameState.settings.play_until_all_finished,
        ),
      });
    }
    if (prevPhaseRef.current === "Game" && gameState?.phase === "Score") {
      if (
        gameState.target_articles.length === gameState.found_articles.length
      ) {
        trackEvent("game-end", { reason: "all targets found" });
        prevPhaseRef.current = gameState?.phase ?? null;
        return;
      }
      const now = Date.now();
      const startMs = new Date(gameState.start_time!).getTime();
      const endMs = startMs + (gameState.game_duration || 0) * 1000;
      const isTimeout = endMs <= now;
      if (isTimeout) {
        trackEvent("game-end", { reason: "timout" });
      } else {
        trackEvent("game-end", { reason: "manuall" });
      }
    }
    prevPhaseRef.current = gameState?.phase ?? null;
  }, [gameState?.phase]);

  useEffect(() => {
    if (!gameState) return;
    trackEvent("wikiparty-sort-change", { lobbyId, sortBy });
  }, [sortBy]);

  const stateReady = !!gameState && !!lobbyState;

  if (!stateReady) {
    return <GameLoadingScreen showGrid={false} />;
  }

  const isHost = currentPlayerId === lobbyState.host_id;
  const currentPlayer =
    currentPlayerId && gameState.players
      ? gameState.players[currentPlayerId]
      : null;
  const requestedArticleId =
    currentPlayer?.past_articles[currentPlayer.past_articles.length - 1]
      ?.article.id ??
    gameState.start?.id ??
    null;
  const waitingForOthers =
    gameState.settings.play_until_all_finished &&
    !!currentPlayer &&
    currentPlayer.found_targets.length === gameState.target_articles.length;
  const targetIndexById = (id: number) =>
    gameState.target_articles.findIndex((article) => article.id === id);
  const displayedStart = pendingArticleMove
    ? pendingArticleMove.start
    : gameState.start;
  const displayedTargets =
    pendingArticleMove?.targets ?? gameState.target_articles;

  const runArticleSearch = async (
    rawQuery: string,
    page: number,
    includeAutocomplete = false,
  ) => {
    const query = rawQuery.trim();
    if (!query) return;

    const request = ++searchRequestRef.current;
    setIsSearchLoading(true);

    if (includeAutocomplete) {
      try {
        const autocomplete = await autocompleteWikipediaArticles(
          gameState.language,
          query,
        );
        if (request !== searchRequestRef.current) return;

        setAutocompleteResults(autocomplete);
        setActiveAutocompleteIndex(-1);
        setIsAutocompleteOpen(autocomplete.length > 0);

        // soeren: this comment is a lie its not cheaper in our case but it could show some useful stuff while typing
        // Autocomplete is substantially cheaper than the fully ranked,
        // counted search. Show its useful results instead of leaving the UI
        // empty while the paginated request finishes.
        updateSearch(autocomplete);
      } catch {
        if (request !== searchRequestRef.current) return;
        setAutocompleteResults([]);
        setIsAutocompleteOpen(false);
      }
    }

    try {
      const result = await searchWikipediaArticles(
        gameState.language,
        query,
        page,
      );
      if (request !== searchRequestRef.current) return;

      updateSearch(result.articles);
      setSearchPage(result.page);
      setSearchHasMore(result.hasMore);
      if (!includeAutocomplete) {
        setAutocompleteResults([]);
        setIsAutocompleteOpen(false);
      }
      searchResultsRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      if (request === searchRequestRef.current && !includeAutocomplete) {
        setAutocompleteResults([]);
        setIsAutocompleteOpen(false);
      }
      console.error("Wikipedia article search failed", error);
    } finally {
      if (request === searchRequestRef.current) setIsSearchLoading(false);
    }
  };

  const selectAutocompleteArticle = (article: Article) => {
    setSearchInput(article.title);
    setAutocompleteResults([]);
    setIsAutocompleteOpen(false);
    void runArticleSearch(article.title, 1);
  };

  if (gameState.phase === "Settings") {
    return (
      <DragDropProvider
        onDragStart={({ operation }) => {
          const article = operation.source?.data.article as Article | undefined;
          setDraggedArticle(article ?? null);
        }}
        onDragEnd={({ operation }) => {
          setDraggedArticle(null);
          const { source, target } = operation;
          if (!source || !target) return;

          const article = source.data.article as Article | undefined;
          if (!article) return;

          const targetIndex = targetIndexById(article.id);
          const kind =
            gameState.start?.id === article.id
              ? "start"
              : targetIndex >= 0
                ? "target"
                : "other";
          switch (target.id) {
            case "start": {
              if (kind === "start") return;

              if (kind === "target") {
                const previousStart = gameState.start;
                setPendingArticleMove({
                  start: article,
                  targets: [
                    ...gameState.target_articles.filter(
                      (targetArticle) => targetArticle.id !== article.id,
                    ),
                    ...(previousStart ? [previousStart] : []),
                  ],
                });
                send(removeArticle(article.id, targetIndex));
                if (previousStart) {
                  send(removeArticle(previousStart.id, null));
                }
                send(setArticle(article, "Start"));
                if (previousStart) {
                  send(setArticle(previousStart, "Target"));
                }
              } else {
                setPendingArticleMove({
                  start: article,
                  targets: gameState.target_articles,
                });
                if (send(setArticle(article, "Start")) && kind === "other") {
                  removeArticleFromSearch(article.id);
                }
              }
              break;
            }

            case "target": {
              if (kind === "target") return;

              if (kind === "start") {
                setPendingArticleMove({
                  start: null,
                  targets: [...gameState.target_articles, article],
                });
                if (!send(removeArticle(article.id, null))) return;
              } else {
                setPendingArticleMove({
                  start: gameState.start,
                  targets: [...gameState.target_articles, article],
                });
              }
              if (send(setArticle(article, "Target")) && kind === "other") {
                removeArticleFromSearch(article.id);
              }
              break;
            }

            default:
              return;
          }
        }}
      >
        <div className={`h-dvh ${theme.bg} flex flex-col relative`}>
          <GeometricGridBackground isDark={isDark} />
          {/* Header */}
          <div
            className={`relative z-50 border-b ${isDark ? "border-white/10 bg-black/50" : "border-black/10 bg-white/50"} backdrop-blur-md`}
          >
            <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <HomeLink />
                <div
                  className={`hidden sm:block w-px h-4 ${isDark ? "bg-white/10" : "bg-black/10"}`}
                />
                <span
                  className={`text-sm font-medium ${isDark ? "text-zinc-400" : "text-gray-500"}`}
                >
                  {t("lobby.setup")}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <CopyLink className="hidden md:block" />
                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              </div>
            </div>
          </div>

          <MobileNav
            currentTab={mobileTabSettings}
            onTabChange={setMobileTabSettings}
            isDark={isDark}
            theme={theme}
            buttons={navbarButtonsSettings}
            className="flex-1"
          >
            {/* Main Content */}
            <div className="relative z-10 p-6">
              <div className="w-full px-4 md:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Players Sidebar */}
                <section
                  className={`md:col-span-3 md:block ${mobileTabSettings === NavButtons.players ? "block" : "hidden"}`}
                >
                  <PlayerList
                    lobbyPlayers={lobbyState.players}
                    gamePlayers={null}
                    showLobbyPoints={true}
                    currentPlayerId={currentPlayerId}
                    theme={theme}
                    isDark={isDark}
                    hostId={lobbyState.host_id}
                    onKickPlayer={(playerId) => send(kickPlayer(playerId))}
                  />
                </section>

                {/* Game Settings */}
                <section
                  className={`md:col-span-5 md:block ${mobileTabSettings === NavButtons.wiki ? "block" : "hidden"}`}
                >
                  <div
                    className={`${theme.sectionBg} ${theme.sectionBorder} sm:rounded-xl sm:p-6`}
                  >
                    <SectionHeader
                      icon={<SvgIcon markup={gearIcon} size={16} />}
                      title={t("lobby.settings.title")}
                      theme={theme}
                      isDark={isDark}
                    />

                    <div className="space-y-6 py-3 border-b border-zinc-800/50">
                      {/* Duration */}
                      <div className="flex flex-col gap-2">
                        <span className={`text-sm ${theme.textSecondary}`}>
                          {t("lobby.settings.gameDuration")}
                        </span>
                        {isHost ? (
                          <DurationControls
                            gameDuration={gameState.game_duration}
                            isDark={isDark}
                            t={t}
                            send={send}
                            setTime={setTime}
                          />
                        ) : (
                          <span className={`text-sm font-medium ${theme.text}`}>
                            {t("lobby.settings.durationMin", {
                              count: Math.floor(gameState.game_duration / 60),
                            })}
                          </span>
                        )}
                      </div>

                      {/* Countries Toggle */}
                      {isLinkBlocked(0) !== null && (
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${theme.textSecondary}`}>
                            {t("lobby.settings.allowCountries")}
                          </span>
                          <SettingsToggle
                            checked={gameState.settings.allow_countries}
                            onChange={(next) => {
                              trackEvent(
                                next
                                  ? "allow-countries-select"
                                  : "allow-countries-unselect",
                              );
                              send(toggleAllowCountries(next));
                            }}
                            disabled={!isHost}
                            isDark={isDark}
                          />
                        </div>
                      )}

                      {/* Allow Search Toggle */}
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${theme.textSecondary}`}>
                          {t("lobby.settings.allowSearch")}
                        </span>
                        <SettingsToggle
                          checked={gameState.settings.allow_search}
                          onChange={(next) => send(toggleAllowSearch(next))}
                          disabled={!isHost}
                          isDark={isDark}
                          testId="allow-search-toggle"
                        />
                      </div>

                      {/* Early-End Trigger */}
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${theme.textSecondary}`}>
                          {t("lobby.settings.earlyEndTrigger")}
                        </span>
                        <div
                          className={`flex items-center gap-2 px-2 py-1 h-9 ${theme.bgSecondary} rounded-lg ${theme.border} border`}
                        >
                          <Select
                            value={
                              gameState.settings.play_until_all_finished
                                ? "all"
                                : "any"
                            }
                            onChange={(e) =>
                              send(
                                togglePlayUntilAllFinished(
                                  e.target.value === "all",
                                ),
                              )
                            }
                            disabled={!isHost}
                            options={[
                              {
                                value: "any",
                                label: t(
                                  "lobby.settings.earlyEndTriggerAnyPlayer",
                                ),
                              },
                              {
                                value: "all",
                                label: t(
                                  "lobby.settings.earlyEndTriggerAllPlayers",
                                ),
                              },
                            ]}
                            className={`${theme.text} text-xs`}
                            optionClassName={theme.selectOption}
                            data-testid="early-end-trigger-select"
                          />
                        </div>
                      </div>

                      {/* Hidden Sections */}
                      <div className="flex flex-col gap-2">
                        <span className={`text-sm ${theme.textSecondary}`}>
                          {t("lobby.settings.hiddenSections")}
                        </span>
                        <HiddenSectionsControls
                          hiddenSections={gameState.settings.hidden_sections}
                          lang={lang}
                          isHost={isHost}
                          isDark={isDark}
                          t={t}
                          send={send}
                          setHiddenSections={setHiddenSections}
                        />
                      </div>
                    </div>

                    {/* Start Article */}
                    <DroppableSection
                      id="start"
                      className="mt-8 space-y-3"
                      activeColorClassName={theme.text}
                    >
                      <SectionHeader
                        icon={<SvgIcon markup={boltIcon} size={16} />}
                        title={t("lobby.startArticle.title")}
                        theme={theme}
                        isDark={isDark}
                      />

                      {displayedStart ? (
                        <DraggableArticleCard
                          dragId="start-article"
                          isHost={isHost}
                          key={displayedStart.id}
                          article={displayedStart}
                          theme={theme}
                          variant="default"
                          onInfoClick={() =>
                            setSelectedArticle({
                              title: displayedStart.title,
                              description: displayedStart.description,
                            })
                          }
                          buttons={
                            isHost ? (
                              <ArticleActionButton
                                variant="link"
                                onClick={() => {
                                  trackEvent("change-start-article");
                                  const action = removeArticle(
                                    displayedStart.id,
                                    null,
                                  );
                                  send(action);
                                }}
                                isDark={isDark}
                              >
                                {t("common.remove")}
                              </ArticleActionButton>
                            ) : null
                          }
                        />
                      ) : (
                        <div
                          className={`p-6 rounded-xl border-2 border-dashed ${isDark ? "border-zinc-800" : "border-gray-200"} text-center`}
                        >
                          <span className={`text-sm ${theme.textMuted}`}>
                            {t("lobby.startArticle.none")}
                          </span>
                        </div>
                      )}
                    </DroppableSection>

                    {/* Target Articles */}
                    <DroppableSection
                      id="target"
                      className="mt-8 space-y-3"
                      activeColorClassName={theme.text}
                    >
                      <SectionHeader
                        icon={<SvgIcon markup={checkCircleIcon} size={16} />}
                        title={t("lobby.targetArticles.titleWithCount", {
                          count: displayedTargets.length,
                        })}
                        theme={theme}
                        isDark={isDark}
                      />

                      {displayedTargets.length > 0 ? (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                          {displayedTargets.map((article, index) => (
                            <DraggableArticleCard
                              dragId={`target-${article.id}`}
                              isHost={isHost}
                              key={article.id}
                              article={article}
                              theme={theme}
                              variant="default"
                              onInfoClick={() =>
                                setSelectedArticle({
                                  title: article.title,
                                  description: article.description,
                                })
                              }
                              buttons={
                                isHost ? (
                                  <ArticleActionButton
                                    variant="link"
                                    onClick={() => {
                                      trackEvent("remove-target-article", {
                                        articleTitle: article.title,
                                      });
                                      const action = removeArticle(
                                        article.id,
                                        index,
                                      );
                                      send(action);
                                    }}
                                    isDark={isDark}
                                  >
                                    {t("common.remove")}
                                  </ArticleActionButton>
                                ) : null
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <div
                          className={`p-6 rounded-xl border-2 border-dashed ${isDark ? "border-zinc-800" : "border-gray-200"} text-center`}
                        >
                          <span className={`text-sm ${theme.textMuted}`}>
                            {t("lobby.targetArticles.none")}
                          </span>
                        </div>
                      )}
                    </DroppableSection>

                    {/* Start Game Button */}
                    {isHost &&
                    gameState.start &&
                    gameState.target_articles.length > 0 ? (
                      <Button
                        variant="primary"
                        onClick={() => {
                          const action = startGame();
                          send(action);
                        }}
                        isDark={isDark}
                        className="mt-8"
                      >
                        {t("lobby.actions.startGame")}
                      </Button>
                    ) : null}
                  </div>
                </section>

                {/* Search */}
                <section
                  className={`md:col-span-4 md:block ${mobileTabSettings === NavButtons.search ? "block" : "hidden"}`}
                >
                  <div className="flex justify-center mb-6">
                    <TabGroup theme={theme}>
                      {tabsButtonsSearch.map((button) => (
                        <TabButton
                          key={button.id}
                          active={tabSearchSuggestions.id === button.id}
                          onClick={() => setTabSearchSuggestions(button)}
                          isDark={isDark}
                          theme={theme}
                        >
                          <span className="flex items-center gap-1.5">
                            {t(button.label)}
                            {button.id === "suggestions" &&
                              gameState.suggested_articles.length > 0 && (
                                <span>
                                  ({gameState.suggested_articles.length})
                                </span>
                              )}
                          </span>
                        </TabButton>
                      ))}
                    </TabGroup>
                  </div>
                  <div
                    className={`${theme.sectionBg} ${theme.sectionBorder} sm:rounded-xl sm:p-6 ${tabSearchSuggestions === NavButtons.search ? "block" : "hidden"}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <SectionHeader
                        icon={
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        }
                        title={t(
                          isHost
                            ? "lobby.actions.searchArticles"
                            : "lobby.actions.suggestArticles",
                        )}
                        theme={theme}
                        isDark={isDark}
                        onClick={() => searchInputRef.current?.focus()}
                      />
                      <button
                        data-testid="random-article-btn"
                        onClick={async () => {
                          trackEvent("random-article-search");
                          const request = ++searchRequestRef.current;
                          setIsSearchLoading(true);
                          try {
                            const articles = await randomWikipediaArticles(
                              gameState.language,
                            );
                            if (request !== searchRequestRef.current) return;
                            updateSearch(articles);
                            setAutocompleteResults([]);
                            setIsAutocompleteOpen(false);
                            setSearchPage(1);
                            setSearchHasMore(false);
                            searchResultsRef.current?.scrollTo({
                              top: 0,
                              behavior: "smooth",
                            });
                          } finally {
                            if (request === searchRequestRef.current) {
                              setIsSearchLoading(false);
                            }
                          }
                        }}
                        className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${isDark ? "bg-zinc-800 border-zinc-700 hover:bg-zinc-700 hover:border-zinc-600 text-zinc-300" : "bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-gray-700"} transition-colors`}
                      >
                        {t("lobby.actions.random")}
                      </button>
                    </div>

                    <div className="relative mb-4">
                      <input
                        ref={searchInputRef}
                        data-testid="article-search-input"
                        type="text"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={isAutocompleteOpen}
                        aria-controls="article-autocomplete-list"
                        aria-activedescendant={
                          isAutocompleteOpen && activeAutocompleteIndex >= 0
                            ? `autocomplete-option-${autocompleteResults[activeAutocompleteIndex]?.id}`
                            : undefined
                        }
                        placeholder={t("lobby.actions.searchPlaceholder")}
                        value={searchInput}
                        onFocus={() => {
                          if (autocompleteBlurTimeoutRef.current) {
                            clearTimeout(autocompleteBlurTimeoutRef.current);
                            autocompleteBlurTimeoutRef.current = null;
                          }
                          setIsAutocompleteOpen(autocompleteResults.length > 0);
                        }}
                        onBlur={() => {
                          if (autocompleteBlurTimeoutRef.current)
                            clearTimeout(autocompleteBlurTimeoutRef.current);
                          // Delay closing so a click on a suggestion or a
                          // Start/Target button lands before the dropdown
                          // unmounts and shifts the results.
                          autocompleteBlurTimeoutRef.current = setTimeout(
                            () => {
                              setIsAutocompleteOpen(false);
                            },
                            150,
                          );
                        }}
                        onKeyDown={(event) => {
                          if (!autocompleteResults.length) return;

                          if (event.key === "ArrowDown") {
                            event.preventDefault();
                            setIsAutocompleteOpen(true);
                            setActiveAutocompleteIndex((index) =>
                              Math.min(
                                index + 1,
                                autocompleteResults.length - 1,
                              ),
                            );
                          } else if (event.key === "ArrowUp") {
                            event.preventDefault();
                            setIsAutocompleteOpen(true);
                            setActiveAutocompleteIndex((index) =>
                              Math.max(index - 1, 0),
                            );
                          } else if (
                            event.key === "Enter" &&
                            isAutocompleteOpen
                          ) {
                            event.preventDefault();
                            if (activeAutocompleteIndex >= 0) {
                              selectAutocompleteArticle(
                                autocompleteResults[activeAutocompleteIndex],
                              );
                            } else {
                              setIsAutocompleteOpen(false);
                            }
                          } else if (event.key === "Escape") {
                            setIsAutocompleteOpen(false);
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSearchInput(value);
                          setSearchPage(1);
                          setSearchHasMore(false);
                          setIsAutocompleteOpen(false);
                          if (searchTimeoutRef.current)
                            clearTimeout(searchTimeoutRef.current);
                          ++searchRequestRef.current;
                          setIsSearchLoading(false);
                          searchTimeoutRef.current = setTimeout(() => {
                            const query = value.trim();
                            if (query.length >= 1) {
                              void runArticleSearch(query, 1, true);
                            } else {
                              updateSearch([]);
                              setAutocompleteResults([]);
                              setIsAutocompleteOpen(false);
                              setSearchHasMore(false);
                            }
                          }, 250);
                        }}
                        className={`w-full pl-4 pr-18 py-3 rounded-lg text-sm ${isDark ? "bg-zinc-900 text-white placeholder-zinc-600 border-zinc-800" : "bg-white text-gray-900 placeholder-gray-400 border-gray-200"} border focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-2 ${isDark ? "focus:ring-offset-zinc-900" : "focus:ring-offset-white"}`}
                      />
                      {isSearchLoading && (
                        <span
                          role="status"
                          aria-label={t("common.loading")}
                          className={`absolute right-10 top-3.5 size-4 animate-spin rounded-full border-2 border-current border-t-transparent ${theme.textMuted}`}
                        />
                      )}
                      {searchInput && (
                        <button
                          type="button"
                          aria-label={t("common.clear")}
                          data-testid="clear-article-search-btn"
                          onClick={() => {
                            if (searchTimeoutRef.current)
                              clearTimeout(searchTimeoutRef.current);
                            ++searchRequestRef.current;
                            setSearchInput("");
                            updateSearch([]);
                            setAutocompleteResults([]);
                            setIsAutocompleteOpen(false);
                            setIsSearchLoading(false);
                            setSearchPage(1);
                            setSearchHasMore(false);
                            searchInputRef.current?.focus();
                          }}
                          className={`absolute right-3 top-2 text-xl ${theme.textMuted}`}
                        >
                          ×
                        </button>
                      )}
                      {isAutocompleteOpen && (
                        <div
                          id="article-autocomplete-list"
                          role="listbox"
                          aria-label={t("lobby.actions.searchArticles")}
                          data-testid="article-autocomplete"
                          className={`mt-2 overflow-hidden rounded-xl border shadow-xl ${theme.bgSecondary} ${theme.border}`}
                        >
                          {autocompleteResults.map((article) => (
                            <button
                              key={article.id}
                              id={`autocomplete-option-${article.id}`}
                              role="option"
                              aria-selected={
                                autocompleteResults[activeAutocompleteIndex]
                                  ?.id === article.id
                              }
                              type="button"
                              data-testid={`autocomplete-article-${article.id}`}
                              onPointerDown={(event) => event.preventDefault()}
                              onMouseEnter={() =>
                                setActiveAutocompleteIndex(
                                  autocompleteResults.indexOf(article),
                                )
                              }
                              onClick={() => selectAutocompleteArticle(article)}
                              className={`flex w-full items-center gap-3 border-b px-4 py-3 text-left text-sm last:border-b-0 ${theme.borderSubtle} ${theme.text} ${autocompleteResults[activeAutocompleteIndex]?.id === article.id ? theme.accentBg : theme.cardHover}`}
                            >
                              <svg
                                className={`size-4 shrink-0 ${theme.textMuted}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-4.35-4.35m1.35-5.15a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
                                />
                              </svg>
                              <span className="min-w-0 flex-1 truncate">
                                {article.title}
                              </span>
                              <svg
                                className={`size-4 shrink-0 ${theme.textMuted}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {localState.searchResults &&
                    localState.searchResults.length > 0 ? (
                      <div
                        ref={searchResultsRef}
                        className="grid grid-cols-1 gap-3 max-h-124 overflow-auto"
                      >
                        {localState.searchResults.map((article) => (
                          <DraggableArticleCard
                            dragId={`search-${article.id}`}
                            isHost={isHost}
                            key={article.id}
                            article={article}
                            theme={theme}
                            onInfoClick={() =>
                              setSelectedArticle({
                                title: article.title,
                                description: article.description,
                              })
                            }
                            buttons={
                              isHost ? (
                                <>
                                  <ArticleActionButton
                                    variant={
                                      gameState.start?.id === article.id
                                        ? "selected"
                                        : "primary"
                                    }
                                    testId={`search-start-btn-${article.id}`}
                                    onClick={() => {
                                      if (gameState.start?.id !== article.id) {
                                        trackEvent("set-start-article", {
                                          articleTitle: article.title,
                                        });
                                        const action = setArticle(
                                          article,
                                          "Start",
                                        );
                                        send(action);
                                      }
                                    }}
                                    isDark={isDark}
                                  >
                                    {t("lobby.actions.start")}
                                  </ArticleActionButton>
                                  <ArticleActionButton
                                    variant={
                                      gameState.target_articles.some(
                                        (item) => item.id === article.id,
                                      )
                                        ? "selected"
                                        : "primary"
                                    }
                                    testId={`search-target-btn-${article.id}`}
                                    onClick={() => {
                                      if (
                                        !gameState.target_articles.some(
                                          (item) => item.id === article.id,
                                        )
                                      ) {
                                        trackEvent("set-target-article", {
                                          articleTitle: article.title,
                                        });
                                        const action = setArticle(
                                          article,
                                          "Target",
                                        );
                                        send(action);
                                      }
                                    }}
                                    isDark={isDark}
                                  >
                                    {t("lobby.actions.target")}
                                  </ArticleActionButton>
                                </>
                              ) : (
                                <>
                                  {gameState.suggested_articles.some(
                                    (item) => item[0].id === article.id,
                                  ) ? (
                                    <ArticleActionButton
                                      variant="muted"
                                      isDark={isDark}
                                    >
                                      {t("lobby.actions.alreadySuggested")}
                                    </ArticleActionButton>
                                  ) : gameState.target_articles.some(
                                      (item) => item.id === article.id,
                                    ) || gameState.start?.id === article.id ? (
                                    <ArticleActionButton
                                      variant="muted"
                                      isDark={isDark}
                                    >
                                      {t("lobby.actions.alreadySelected")}
                                    </ArticleActionButton>
                                  ) : (
                                    <ArticleActionButton
                                      onClick={() => {
                                        trackEvent("suggest-article", {
                                          articleTitle: article.title,
                                        });
                                        const action = suggestArticle(article);
                                        send(action);
                                      }}
                                      testId={`search-suggest-btn-${article.id}`}
                                      isDark={isDark}
                                    >
                                      {t("lobby.actions.suggest")}
                                    </ArticleActionButton>
                                  )}
                                </>
                              )
                            }
                          />
                        ))}
                      </div>
                    ) : null}

                    {(searchPage > 1 || searchHasMore) && (
                      <nav
                        aria-label={t("lobby.actions.currentPage", {
                          page: searchPage,
                        })}
                        aria-busy={isSearchLoading}
                        data-testid="article-search-pagination"
                        className={`mt-5 flex items-center justify-between gap-3 border-t pt-4 ${theme.borderSubtle}`}
                      >
                        <button
                          type="button"
                          data-testid="article-search-previous-page"
                          disabled={searchPage <= 1 || isSearchLoading}
                          onClick={() =>
                            void runArticleSearch(searchInput, searchPage - 1)
                          }
                          className={`inline-flex min-w-10 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-40 ${theme.buttonSecondary} ${theme.buttonSecondaryText} ${theme.border}`}
                        >
                          <svg
                            className="size-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          <span className="hidden sm:inline">
                            {t("lobby.actions.previousPage")}
                          </span>
                        </button>
                        <span
                          aria-live="polite"
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium tabular-nums ${theme.bgSecondary} ${theme.textSecondary} ${theme.border}`}
                        >
                          {t("lobby.actions.currentPage", {
                            page: searchPage,
                          })}
                        </span>
                        <button
                          type="button"
                          data-testid="article-search-next-page"
                          disabled={!searchHasMore || isSearchLoading}
                          onClick={() =>
                            void runArticleSearch(searchInput, searchPage + 1)
                          }
                          className={`inline-flex min-w-10 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-40 ${theme.buttonSecondary} ${theme.buttonSecondaryText} ${theme.border}`}
                        >
                          <span className="hidden sm:inline">
                            {t("lobby.actions.nextPage")}
                          </span>
                          <svg
                            className="size-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </nav>
                    )}
                  </div>

                  {/* Suggestions */}
                  {/* TODO: maybe suggestions should only be clickable if there are suggestions   */}
                  {/* {gameState.suggested_articles.length > 0 ? ( */}
                  <div
                    className={`${theme.sectionBg} ${theme.sectionBorder} rounded-xl p-6 mt-6 ${tabSearchSuggestions === NavButtons.suggestions ? "block" : "hidden"}`}
                  >
                    <div className="mb-6">
                      <SectionHeader
                        icon={
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                          </svg>
                        }
                        title={t("lobby.suggestions")}
                        theme={theme}
                        isDark={isDark}
                      />
                    </div>
                    <div className="space-y-2 overflow-auto max-h-144">
                      {gameState.suggested_articles.map((suggestion) => {
                        const article = suggestion[0];
                        const suggester_id = suggestion[1];
                        return (
                          <DraggableArticleCard
                            dragId={`suggestion-${article.id}`}
                            isHost={isHost}
                            key={article.id}
                            article={article}
                            theme={theme}
                            variant="default"
                            onInfoClick={() =>
                              setSelectedArticle({
                                title: article.title,
                                description: article.description,
                              })
                            }
                            buttons={
                              isHost ? (
                                <>
                                  <ArticleActionButton
                                    onClick={() => {
                                      const action = setArticle(
                                        article,
                                        "Start",
                                      );
                                      send(action);
                                    }}
                                    testId={`suggestion-start-btn-${article.id}`}
                                    isDark={isDark}
                                  >
                                    {t("lobby.actions.start")}
                                  </ArticleActionButton>
                                  <ArticleActionButton
                                    onClick={() => {
                                      const action = setArticle(
                                        article,
                                        "Target",
                                      );
                                      send(action);
                                    }}
                                    testId={`suggestion-target-btn-${article.id}`}
                                    isDark={isDark}
                                  >
                                    {t("lobby.actions.target")}
                                  </ArticleActionButton>
                                  <ArticleActionButton
                                    onClick={() => {
                                      const action = removeSuggestion(
                                        article.id,
                                        suggester_id,
                                      );
                                      send(action);
                                    }}
                                    isDark={isDark}
                                  >
                                    {t("common.remove")}
                                  </ArticleActionButton>
                                </>
                              ) : null
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                  {/* ) : null} */}
                </section>
              </div>
            </div>
          </MobileNav>
          <ArticleDialog
            isOpen={!!selectedArticle}
            onClose={() => setSelectedArticle(null)}
            title={selectedArticle?.title ?? ""}
            description={selectedArticle?.description ?? null}
            isDark={isDark}
          />
        </div>
        <DragOverlay dropAnimation={null}>
          {draggedArticle ? (
            <ArticleCard
              article={draggedArticle}
              theme={theme}
              variant={
                gameState.start?.id === draggedArticle.id ? "start" : "default"
              }
              className="cursor-grabbing shadow-lg"
            />
          ) : null}
        </DragOverlay>
      </DragDropProvider>
    );
  }

  if (gameState.phase === "Game" && !waitingForOthers) {
    return (
      <div className={`h-dvh ${theme.bg} flex flex-col relative`}>
        {/* Header */}
        <div
          className={`relative z-50 shrink-0 border-b ${isDark ? "border-white/10 bg-black/50" : "border-black/10 bg-white/50"} backdrop-blur-md`}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            {/* Left Section */}
            <div className="flex items-center gap-3">
              <HomeLink textClassName="hidden md:block" />
              <ThemeToggle
                isDark={isDark}
                onToggle={toggleTheme}
                className="hidden md:block"
              />
            </div>

            {/* Center Section - Takes all available space for target badges to wrap */}
            <div className="flex-1 flex items-center justify-center gap-3 px-4 min-w-0">
              <div className="hidden md:flex flex-wrap items-center justify-center gap-1.5">
                {(() => {
                  const clientFoundTargets = new Set(
                    currentPlayer?.found_targets.map((fa) => fa.article.id),
                  );
                  const otherFoundArticles = new Set(
                    gameState.found_articles?.map((fa) => fa.id),
                  );
                  return (
                    <TargetBadges
                      targets={gameState.target_articles}
                      isFound={(id: number) => clientFoundTargets.has(id)}
                      theme={theme}
                      isDark={isDark}
                      renderBadgeContent={(target, isFound) => {
                        const foundByOther = otherFoundArticles.has(target.id);
                        if (isFound) {
                          return <SvgIcon markup={checkIcon} size={14} />;
                        } else if (foundByOther) {
                          return "10";
                        } else {
                          return "15";
                        }
                      }}
                    />
                  );
                })()}
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4">
              <Timer
                startTime={gameState.start_time!}
                duration={gameState.game_duration}
                isDark={isDark}
              />
              <div className="hidden md:block">
                <CopyLink />
              </div>
              {isHost ? (
                <button
                  onClick={() => {
                    const action = endGame();
                    send(action);
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 ${
                    isDark
                      ? "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
                      : "bg-gray-100 text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-900"
                  }`}
                >
                  {t("lobby.actions.endGame")}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <MobileNav
          currentTab={mobileTabIngame}
          onTabChange={setMobileTabIngame}
          isDark={isDark}
          theme={theme}
          buttons={navbarButtonsIngame}
          className="flex-1"
        >
          {/* Main Game Area */}
          <div className="relative z-10 grid md:grid-cols-[minmax(0,18rem)_1fr_auto] overflow-hidden min-h-0 h-full">
            {/* Left Sidebar - Players */}
            <section
              className={`md:block px-4 py-4 h-full overflow-auto ${mobileTabIngame === NavButtons.players ? "block" : "hidden"}`}
            >
              <PlayerList
                lobbyPlayers={lobbyState.players}
                gamePlayers={gameState.players}
                showLobbyPoints={false}
                currentPlayerId={currentPlayerId}
                theme={theme}
                isDark={isDark}
                hostId={lobbyState.host_id}
                onKickPlayer={(playerId) => send(kickPlayer(playerId))}
              />
            </section>

            {/* Center - WikiPage */}
            <section
              className={`h-full overflow-hidden flex justify-center ${mobileTabIngame === NavButtons.wiki ? "block" : "hidden md:block"}`}
            >
              <Wikipage
                isDark={isDark}
                send={send}
                language={gameState.language}
                requestedArticleId={requestedArticleId}
                isLinkBlocked={isLinkBlocked}
                allowSearch={gameState.settings.allow_search}
                hiddenSectionTitles={gameState.settings.hidden_sections}
              />
            </section>

            {/* Right Sidebar - Targets (mobile only) */}
            <section
              className={`px-4 py-4 space-y-2 md:hidden ${mobileTabIngame === NavButtons.targets ? "block" : "hidden"}`}
            >
              <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
                {t("lobby.targetArticles.title")}
              </h3>
              <div className="flex flex-col justify-start gap-2">
                {(() => {
                  const clientFoundTargets = new Set(
                    currentPlayer?.found_targets.map((fa) => fa.article.id),
                  );
                  const otherFoundArticles = new Set(
                    gameState.found_articles?.map((fa) => fa.id),
                  );
                  return (
                    <TargetBadges
                      targets={gameState.target_articles}
                      isFound={(id: number) => clientFoundTargets.has(id)}
                      theme={theme}
                      isDark={isDark}
                      showSeparators={false}
                      renderBadgeContent={(target, isFound) => {
                        const foundByOther = otherFoundArticles.has(target.id);
                        if (isFound) {
                          return <SvgIcon markup={checkIcon} size={14} />;
                        } else if (foundByOther) {
                          return "10";
                        } else {
                          return "15";
                        }
                      }}
                    />
                  );
                })()}
              </div>
            </section>
          </div>
        </MobileNav>
      </div>
    );
  }

  if (gameState.phase === "Score" || waitingForOthers) {
    const playerEntries = Object.entries(gameState.players).sort(
      (a, b) => b[1].points - a[1].points,
    );
    const anyTargetsFound = Object.values(gameState.players).some(
      (p) => p.found_targets.length > 0,
    );
    const winner = anyTargetsFound ? playerEntries[0] : null;
    const isCurrentPlayerWinner = winner && winner[0] === currentPlayerId;

    const showRecordIcons =
      Object.keys(gameState.players).length > 1 &&
      gameState.phase === "Score" &&
      gameState.settings.play_until_all_finished;

    const finishedAll = (data: (typeof playerEntries)[number][1]) =>
      data.found_targets.length === gameState.target_articles.length;
    const finishTime = (data: (typeof playerEntries)[number][1]) =>
      data.found_targets[data.found_targets.length - 1]?.found_time[0] ??
      Infinity;

    const foundPlayers = playerEntries.filter(([, data]) => finishedAll(data));
    const fastestPlayerId =
      foundPlayers.length > 0
        ? foundPlayers.reduce((a, b) => {
            const timeDiff = finishTime(a[1]) - finishTime(b[1]);
            if (timeDiff !== 0) return timeDiff < 0 ? a : b;
            return a[1].past_articles.length < b[1].past_articles.length
              ? a
              : b;
          })[0]
        : null;
    const fewestMovesPlayerId =
      foundPlayers.length > 0
        ? foundPlayers.reduce((a, b) => {
            const movesDiff =
              a[1].past_articles.length - b[1].past_articles.length;
            if (movesDiff !== 0) return movesDiff < 0 ? a : b;
            return finishTime(a[1]) < finishTime(b[1]) ? a : b;
          })[0]
        : null;

    const displayEntries = [...playerEntries].sort((a, b) => {
      if (sortBy === "moves" || sortBy === "time") {
        const aFinished = finishedAll(a[1]);
        const bFinished = finishedAll(b[1]);
        if (aFinished !== bFinished) return aFinished ? -1 : 1;
        if (aFinished) {
          if (sortBy === "time") {
            const timeDiff = finishTime(a[1]) - finishTime(b[1]);
            if (timeDiff !== 0) return timeDiff;
            return a[1].past_articles.length - b[1].past_articles.length;
          }
          const movesDiff =
            a[1].past_articles.length - b[1].past_articles.length;
          if (movesDiff !== 0) return movesDiff;
          return finishTime(a[1]) - finishTime(b[1]);
        }
        return b[1].points - a[1].points;
      }
      return b[1].points - a[1].points;
    });

    const winnerId = winner ? winner[0] : null;
    const topThreeIds = new Set(
      anyTargetsFound ? playerEntries.slice(0, 3).map(([id]) => id) : [],
    );

    const timelinePlayers = playerEntries.map(([playerId, data]) => {
      const lobbyPlayer = lobbyState.players[playerId];
      const name = lobbyPlayer?.name || `Player ${playerId.slice(0, 4)}`;
      const articles = data.past_articles.map((pa) => {
        const [seconds, nanos] = pa.found_time;
        const duration = seconds + nanos / 1000000000;
        return {
          text: pa.article.title,
          duration,
          type: pa.is_target ? ("highlight" as const) : undefined,
        };
      });
      return { name, articles };
    });

    return (
      <div
        className={`h-dvh ${theme.bg} flex flex-col relative overflow-hidden`}
      >
        <GeometricGridBackground isDark={isDark} />

        <MobileNav
          currentTab={mobileTabEndscreen}
          onTabChange={setMobileTabEndscreen}
          isDark={isDark}
          theme={theme}
          buttons={navbarButtonsEndscreen}
          className="flex-1 overflow-auto"
        >
          {/* Header - Full width like in Game phase */}
          <div
            className={`relative z-50 shrink-0 border-b ${isDark ? "border-white/10 bg-black/50" : "border-black/10 bg-white/50"} backdrop-blur-md`}
          >
            <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <HomeLink />
                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              </div>
            </div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
            {/* Winner Section */}
            {gameState.phase === "Game" ? (
              <div className="text-center mb-10">
                <h1
                  className={`text-3xl md:text-5xl font-bold tracking-tight ${theme.text} mb-2`}
                >
                  {t("lobby.score.youFoundAll")}
                </h1>
                <p className={`text-lg ${theme.textMuted}`}>
                  {t("lobby.score.waitingForOthers")}
                </p>
              </div>
            ) : anyTargetsFound && winner ? (
              <div className="text-center mb-10">
                {/* Winner Name */}
                <h1
                  className={`text-3xl md:text-5xl font-bold tracking-tight ${theme.text} mb-2`}
                >
                  {isCurrentPlayerWinner
                    ? t("lobby.score.youWon")
                    : t("lobby.score.playerWins", {
                        name: lobbyState.players[winner[0]].name,
                      })}
                </h1>
                <p className={`text-lg ${theme.textMuted}`}>
                  {t("lobby.score.stats", {
                    points: winner[1].points,
                    targets: winner[1].found_targets.length,
                  })}
                </p>
              </div>
            ) : (
              <div className="text-center mb-10">
                {/* Game Over Title */}
                <h1
                  className={`text-3xl md:text-5xl font-bold tracking-tight ${theme.text} mb-2`}
                >
                  {t("lobby.score.gameOver")}
                </h1>
                <p className={`text-lg ${theme.textMuted}`}>
                  {t("lobby.score.nobodyFound")}
                </p>
              </div>
            )}

            {/* Desktop Tab Navigation */}
            <div className="hidden md:flex justify-center mb-8">
              <TabGroup theme={theme}>
                {navbarButtonsEndscreen.map((button) => (
                  <TabButton
                    key={button.id}
                    active={mobileTabEndscreen.id === button.id}
                    onClick={() => {
                      if (button.id === "scoreboard")
                        trackEvent("results-scoreboard-tab");
                      if (button.id === "playerPaths")
                        trackEvent("results-paths-tab");
                      if (button.id === "targetsFound")
                        trackEvent("results-targets-tab");
                      setMobileTabEndscreen(button);
                    }}
                    isDark={isDark}
                    theme={theme}
                  >
                    {t(button.label)}
                  </TabButton>
                ))}
              </TabGroup>
            </div>

            {/* Scoreboard */}
            <section
              className={
                mobileTabEndscreen === NavButtons.scoreboard
                  ? "block"
                  : "hidden"
              }
            >
              {/* Podium for Top 3 - only show if someone found targets */}
              {anyTargetsFound && (
                <Podium
                  players={playerEntries.map(([id, data]) => ({
                    id,
                    name:
                      lobbyState.players[id]?.name ||
                      `Player ${id.slice(0, 4)}`,
                    image: lobbyState.players[id]?.image,
                    points: data.points,
                    targetsFound: data.found_targets.length,
                  }))}
                  isDark={isDark}
                  currentPlayerId={currentPlayerId}
                  className="mb-8"
                />
              )}

              <div
                className={`${theme.sectionBg} ${theme.sectionBorder} rounded-2xl overflow-hidden`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between gap-4">
                    <SectionHeader
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                      }
                      title={
                        gameState.phase === "Score"
                          ? t("lobby.score.finalStandings")
                          : t("lobby.score.currentStandings")
                      }
                      theme={theme}
                      isDark={isDark}
                    />
                    {gameState.phase === "Game" && gameState.start_time && (
                      <div className="flex items-center gap-3">
                        {isHost && (
                          <button
                            onClick={() => {
                              const action = endGame();
                              send(action);
                            }}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 ${
                              isDark
                                ? "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
                                : "bg-gray-100 text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-900"
                            }`}
                          >
                            {t("lobby.actions.endForEveryone")}
                          </button>
                        )}
                        <Timer
                          startTime={gameState.start_time}
                          duration={gameState.game_duration}
                          isDark={isDark}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <div
                      className={`inline-flex rounded-full border ${
                        isDark ? "border-zinc-700" : "border-gray-200"
                      } p-0.5`}
                    >
                      {(
                        [
                          {
                            key: "points",
                            label: t("lobby.score.points"),
                            testId: "sort-points",
                          },
                          {
                            key: "moves",
                            label: t("lobby.score.fewestMoves"),
                            testId: "sort-moves",
                          },
                          {
                            key: "time",
                            label: t("lobby.score.fastestTime"),
                            testId: "sort-time",
                          },
                        ] as const
                      ).map((option) => (
                        <button
                          key={option.key}
                          data-testid={option.testId}
                          onClick={() => setSortBy(option.key)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            sortBy === option.key
                              ? isDark
                                ? "bg-white text-black"
                                : "bg-gray-900 text-white"
                              : isDark
                                ? "text-zinc-400 hover:text-white"
                                : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6 space-y-3">
                  {displayEntries.map(([playerId, data], index) => {
                    const isWinner = anyTargetsFound && playerId === winnerId;
                    const isTopThree = topThreeIds.has(playerId);
                    const finishedAll =
                      data.found_targets.length ===
                      gameState.target_articles.length;
                    const finishTime = finishedAll
                      ? data.found_targets[data.found_targets.length - 1]
                          .found_time[0]
                      : null;
                    return (
                      <div
                        key={playerId}
                        className={`p-4 rounded-xl flex justify-between items-center transition-all duration-300 ${
                          isWinner
                            ? isDark
                              ? "bg-white text-black shadow-lg shadow-white/10"
                              : "bg-gray-900 text-white shadow-lg"
                            : isTopThree
                              ? isDark
                                ? "bg-zinc-800/80 border border-zinc-700"
                                : "bg-gray-100 border border-gray-200"
                              : isDark
                                ? "bg-zinc-900/50 hover:bg-zinc-800/50"
                                : "bg-white hover:bg-gray-50"
                        }`}
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex items-center gap-4">
                          <span
                            className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                              isWinner
                                ? isDark
                                  ? "bg-black text-white"
                                  : "bg-white text-black"
                                : isDark
                                  ? "bg-zinc-800 text-zinc-400"
                                  : "bg-gray-200 text-gray-500"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <ProfileAvatar
                            image={lobbyState.players[playerId]?.image}
                            name={
                              lobbyState.players[playerId]?.name || "Player"
                            }
                            size="sm"
                          />
                          <div>
                            <span
                              className={`font-medium ${playerId === currentPlayerId ? "font-semibold" : ""} ${isWinner ? (isDark ? "text-black" : "text-white") : isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {lobbyState.players[playerId].name.slice(0, 20)}
                              {playerId === currentPlayerId && (
                                <span
                                  className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDark ? "bg-zinc-700 text-zinc-300" : "bg-gray-200 text-gray-600"}`}
                                >
                                  {t("common.you")}
                                </span>
                              )}
                              {showRecordIcons &&
                                playerId === fastestPlayerId &&
                                playerId === fewestMovesPlayerId && (
                                  <img
                                    src="/crown.svg"
                                    className="h-5 ml-2 inline-block"
                                    alt=""
                                  />
                                )}
                              {showRecordIcons &&
                                playerId === fastestPlayerId &&
                                playerId !== fewestMovesPlayerId && (
                                  <img
                                    src="/clock_without_crown.svg"
                                    className="h-5 ml-2 inline-block"
                                    alt=""
                                  />
                                )}
                              {showRecordIcons &&
                                playerId === fewestMovesPlayerId &&
                                playerId !== fastestPlayerId && (
                                  <img
                                    src="/mouse-click-icon.svg"
                                    className="h-5 ml-2 inline-block"
                                    alt=""
                                  />
                                )}
                            </span>
                            <div className="mt-1">
                              <ProviderBadge
                                provider={
                                  lobbyState.players[playerId]?.provider
                                }
                              />
                            </div>
                            {isWinner && (
                              <div
                                className={`text-xs mt-0.5 ${isDark ? "text-zinc-600" : "text-gray-400"}`}
                              >
                                {t("lobby.score.winner")}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          {!finishedAll && gameState.phase === "Score" && (
                            <span
                              data-testid={`score-time-${playerId}`}
                              title={t("lobby.score.dnfTitle")}
                              className={`text-sm ${isWinner ? (isDark ? "text-zinc-600" : "text-gray-300") : theme.textMuted}`}
                            >
                              {t("lobby.score.dnf")}
                            </span>
                          )}
                          {!finishedAll && gameState.phase === "Game" && (
                            <span
                              data-testid={`score-moves-${playerId}`}
                              title={t("lobby.score.stillPlaying")}
                              className={`text-sm ${isWinner ? (isDark ? "text-zinc-600" : "text-gray-300") : theme.textMuted}`}
                            >
                              {t("lobby.score.moves", {
                                count: data.past_articles.length,
                              })}
                            </span>
                          )}
                          {finishedAll && (
                            <span
                              data-testid={`score-moves-${playerId}`}
                              className={`text-sm ${isWinner ? (isDark ? "text-zinc-600" : "text-gray-300") : theme.textMuted}`}
                            >
                              {t("lobby.score.moves", {
                                count: data.past_articles.length,
                              })}
                            </span>
                          )}
                          {finishedAll && (
                            <span
                              data-testid={`score-time-${playerId}`}
                              title={t("lobby.score.time")}
                              className={`text-sm ${isWinner ? (isDark ? "text-zinc-600" : "text-gray-300") : theme.textMuted}`}
                            >
                              {formatDurationCompact(finishTime!)}
                            </span>
                          )}
                          {!finishedAll && (
                            <span
                              className={`text-sm ${isWinner ? (isDark ? "text-zinc-600" : "text-gray-300") : theme.textMuted}`}
                            >
                              {t("lobby.score.targetsCount", {
                                count: data.found_targets.length,
                              })}
                            </span>
                          )}
                          <span
                            className={`text-2xl font-bold ${isWinner ? "" : theme.text}`}
                          >
                            {data.points}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Targets Found */}
            <section
              className={
                mobileTabEndscreen === NavButtons.targetsFound
                  ? "block"
                  : "hidden"
              }
            >
              <div
                className={`${theme.sectionBg} ${theme.sectionBorder} rounded-2xl p-6`}
              >
                <SectionHeader
                  icon={<SvgIcon markup={checkCircleIcon} size={20} />}
                  title={t("lobby.score.targetsFound")}
                  theme={theme}
                  isDark={isDark}
                />

                {anyTargetsFound ? (
                  <div className="mt-6 space-y-4">
                    {(() => {
                      // Build a map of target -> who found it and when
                      const targetFinders = new Map<
                        number,
                        Array<{
                          playerId: string;
                          playerName: string;
                          playerImage?: string | null;
                          playerProvider?: string | null;
                          foundTime: number;
                        }>
                      >();

                      Object.entries(gameState.players).forEach(
                        ([playerId, data]) => {
                          data.found_targets.forEach((ft) => {
                            const [seconds] = ft.found_time;
                            if (!targetFinders.has(ft.article.id)) {
                              targetFinders.set(ft.article.id, []);
                            }
                            targetFinders.get(ft.article.id)!.push({
                              playerId,
                              playerName:
                                lobbyState.players[playerId]?.name ||
                                `Player ${playerId.slice(0, 4)}`,
                              playerImage: lobbyState.players[playerId]?.image,
                              playerProvider:
                                lobbyState.players[playerId]?.provider,
                              foundTime: seconds,
                            });
                          });
                        },
                      );

                      return gameState.target_articles.map((article) => {
                        const finders = targetFinders.get(article.id) || [];
                        const wasFound = finders.length > 0;
                        const sortedFinders = finders.sort(
                          (a, b) => a.foundTime - b.foundTime,
                        );

                        return (
                          <div
                            key={article.id}
                            className={`p-4 rounded-xl transition-all ${
                              wasFound
                                ? isDark
                                  ? "bg-zinc-800/80 border border-zinc-700"
                                  : "bg-white border border-gray-200 shadow-sm"
                                : isDark
                                  ? "bg-zinc-900/30 border border-zinc-800/50 opacity-50"
                                  : "bg-gray-50 border border-gray-100 opacity-50"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {wasFound ? (
                                  <div
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-600"}`}
                                  >
                                    <SvgIcon markup={checkIcon} size={16} />
                                  </div>
                                ) : (
                                  <div
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? "bg-zinc-800 text-zinc-600" : "bg-gray-200 text-gray-400"}`}
                                  >
                                    <SvgIcon markup={closeIcon} size={16} />
                                  </div>
                                )}
                                <span
                                  className={`font-medium ${wasFound ? theme.text : theme.textMuted}`}
                                >
                                  {article.title}
                                </span>
                              </div>
                              <span
                                className={`text-sm px-3 py-1 rounded-full ${
                                  wasFound
                                    ? isDark
                                      ? "bg-zinc-900 text-zinc-300"
                                      : "bg-gray-100 text-gray-700"
                                    : isDark
                                      ? "bg-zinc-900 text-zinc-600"
                                      : "bg-gray-200 text-gray-400"
                                }`}
                              >
                                {t("lobby.score.finders", {
                                  count: finders.length,
                                })}
                              </span>
                            </div>

                            {wasFound && (
                              <div
                                className={`ml-11 space-y-2 ${isDark ? "border-l-2 border-zinc-700 pl-4" : "border-l-2 border-gray-200 pl-4"}`}
                              >
                                {sortedFinders.map((finder, idx) => (
                                  <div
                                    key={`${finder.playerId}-${idx}`}
                                    className="flex items-center justify-between text-sm"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={theme.textMuted}>
                                        {idx + 1}.
                                      </span>
                                      <ProfileAvatar
                                        image={finder.playerImage}
                                        name={finder.playerName}
                                        size="sm"
                                      />
                                      <span
                                        className={
                                          finder.playerId === currentPlayerId
                                            ? `font-semibold ${theme.text}`
                                            : theme.textMuted
                                        }
                                      >
                                        {finder.playerName}
                                        {finder.playerId ===
                                          currentPlayerId && (
                                          <span
                                            className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDark ? "bg-zinc-700 text-zinc-300" : "bg-gray-200 text-gray-600"}`}
                                          >
                                            {t("common.you")}
                                          </span>
                                        )}
                                      </span>
                                      <ProviderBadge
                                        provider={finder.playerProvider}
                                      />
                                    </div>
                                    <span
                                      className={`text-xs ${theme.textMuted}`}
                                    >
                                      {Math.floor(finder.foundTime / 60)}:
                                      {String(finder.foundTime % 60).padStart(
                                        2,
                                        "0",
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div
                    className={`mt-6 p-8 text-center rounded-xl ${isDark ? "bg-zinc-900/50" : "bg-gray-50"}`}
                  >
                    <div
                      className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDark ? "bg-zinc-800" : "bg-gray-200"}`}
                    >
                      <svg
                        className={`w-8 h-8 ${theme.textMuted}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className={`${theme.textMuted} text-lg mb-1`}>
                      {t("lobby.score.noTargetsFound")}
                    </p>
                    <p className={`text-sm ${theme.textMuted}`}>
                      {t("lobby.score.betterLuck")}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Player Paths */}
            <section
              className={
                mobileTabEndscreen === NavButtons.playerPaths
                  ? "block"
                  : "hidden"
              }
            >
              <div
                className={`${theme.sectionBg} ${theme.sectionBorder} rounded-2xl p-6`}
              >
                <SectionHeader
                  icon={<SvgIcon markup={mapIcon} size={20} />}
                  title={t("lobby.score.playerPaths")}
                  theme={theme}
                  isDark={isDark}
                />

                {timelinePlayers.length > 0 &&
                timelinePlayers.some((p) => p.articles.length > 0) ? (
                  <div className="mt-4">
                    <Timeline players={timelinePlayers} isDark={isDark} />
                  </div>
                ) : (
                  <EmptyState
                    isDark={isDark}
                    message={t("lobby.score.noPathData")}
                    icon={
                      <SvgIcon markup={mapIcon} size={32} strokeWidth={1.5} />
                    }
                  />
                )}
              </div>
            </section>

            {/* Play Again / End for Others Button */}
            {isHost && gameState.phase === "Score" && (
              <div className="mt-8">
                <Button
                  variant="primary"
                  onClick={() => send(restartGame())}
                  isDark={isDark}
                  className="w-full py-4 text-lg"
                >
                  {t("lobby.actions.playAgain")}
                </Button>
              </div>
            )}

            <div className="mt-8">
              <GeoBingoCard isDark={isDark} variant="game" />
            </div>
          </div>
        </MobileNav>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${theme.bg} flex items-center justify-center`}
    >
      <p className={theme.textMuted}>{t("lobby.unknownPhase")}</p>
    </div>
  );
}
